import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Link } from "react-router-dom";
import { UserCheck, Fingerprint, Siren, ClipboardCheck, ShieldAlert, Bus } from "lucide-react";

import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import Marquee from "@/components/Marquee";
import CTA from "@/components/CTA";
import SEO from "@/components/SEO";
import VideoBackground from "@/components/VideoBackground";
import { TGWordmark } from "@/components/TGWordmark";
import { RevealLines, FadeUp, ClipReveal } from "@/components/Reveal";
import { IMAGES, CHAPTERS, ROLES, STATS, SECURITY, SAFETY_FEATURES, VIDEOS, PEOPLE, RYAN_PHOTO, testguardHref } from "@/lib/constants";

const SAFETY_ICONS = { UserCheck, Fingerprint, Siren, ClipboardCheck, ShieldAlert, Bus };

const EASE = [0.16, 1, 0.3, 1];

function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-bone pt-36 pb-20 md:pt-44 md:pb-28">
      {/* faint backdrop wordmark */}
      <div className="pointer-events-none absolute -right-10 top-1/2 hidden -translate-y-1/2 select-none lg:block">
        <span className="numeral-outline font-display text-[22vw] font-900 leading-none">CP</span>
      </div>

      <div className="relative mx-auto grid max-w-7xl gap-14 px-6 md:px-8 lg:grid-cols-12 lg:items-center">
        <div className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: EASE }}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-ink/15 bg-white/60 px-4 py-1.5 text-xs font-600 uppercase tracking-widest text-ink/70 backdrop-blur"
            data-testid="hero-eyebrow"
          >
            <span className="h-2 w-2 rounded-full bg-leaf" /> K-12 Operations Platform
          </motion.div>

          <h1 className="font-display text-[clamp(2.8rem,7vw,5.2rem)] font-900 leading-[0.95] tracking-tightest text-ink">
            <RevealLines lines={["Smart", "Dismissal.", "Safer Schools."]} stagger={0.1} />
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.55 }}
            className="mt-8 max-w-md text-lg leading-relaxed text-ink/70"
            data-testid="hero-subtitle"
          >
            Real-time tracking, verified pickups, attendance and family communication, the whole
            school day, running in one place.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <CTA to="/trial" variant="leaf" dataTestId="hero-trial-btn">Start a 45–90 day trial</CTA>
            <CTA to="/demo" variant="outline" dataTestId="hero-demo-btn">See how it works</CTA>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 0.9 }}
            className="mt-12 flex items-center gap-4"
          >
            <div className="flex -space-x-3">
              {PEOPLE.map((src, i) => (
                <img
                  key={i}
                  src={src}
                  alt=""
                  className="h-10 w-10 rounded-full border-2 border-bone object-cover"
                  data-testid={`hero-avatar-${i}`}
                />
              ))}
            </div>
            <div className="text-sm leading-tight text-ink/70">
              <span className="font-700 text-ink">Tested by school teachers &amp; educational administrators</span>
              <br />
              for safer, faster daily operations.
            </div>
          </motion.div>
        </div>

        {/* Product visual with parallax */}
        <div className="relative lg:col-span-6">
          <ClipReveal className="overflow-hidden rounded-[2rem]">
            <motion.div style={{ scale }} className="relative">
              <motion.img
                style={{ y: yImg }}
                src={IMAGES.school}
                alt="Modern school campus"
                className="h-[440px] w-full object-cover md:h-[560px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-ink/50 via-transparent to-transparent" />
            </motion.div>
          </ClipReveal>

          {/* floating glass dashboard card */}
          <motion.div
            style={{ y: yCard }}
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: EASE, delay: 0.8 }}
            className="absolute -bottom-8 -left-4 w-[280px] rounded-2xl border border-white/40 bg-white/80 p-5 shadow-[0_30px_60px_-25px_rgba(0,34,68,0.5)] backdrop-blur-2xl md:-left-10"
            data-testid="hero-dashboard-card"
          >
            <div className="flex items-center justify-between">
              <span className="text-xs font-600 uppercase tracking-widest text-ink/50">Student Dismissal</span>
              <span className="flex items-center gap-1.5 text-xs font-700 text-leaf-600">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-leaf" /> Live
              </span>
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2">
              {[["230", "Total"], ["228", "Here"], ["2", "Gone"]].map(([v, l]) => (
                <div key={l} className="rounded-xl bg-ink/5 p-3">
                  <div className="font-display text-2xl font-800 text-ink">{v}</div>
                  <div className="text-[11px] uppercase tracking-wide text-ink/50">{l}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center justify-between rounded-xl border border-leaf/30 bg-leaf/10 px-3 py-2.5">
              <div className="flex items-center gap-2">
                <img src={RYAN_PHOTO} alt="Ryan A." className="h-7 w-7 rounded-full object-cover" data-testid="ryan-avatar" />
                <div className="text-xs">
                  <div className="font-700 text-ink">Ryan A.</div>
                  <div className="text-ink/50">Signature required</div>
                </div>
              </div>
              <span className="text-leaf-600">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" /></svg>
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Chapters() {
  return (
    <section id="platform" className="relative bg-bone pt-12 pb-12 md:pt-16 md:pb-16">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <FadeUp className="mb-12 max-w-2xl md:mb-16">
          <span className="text-xs font-600 uppercase tracking-widest text-leaf-600">The Platform</span>
          <h2 className="mt-4 font-display text-[clamp(2rem,4.5vw,3.4rem)] font-800 leading-[1.02] tracking-tightest text-ink">
            Everything the school day needs, without the whole story.
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-ink/60">
            A modular system with role-based access for administrators, teachers, staff, parents and
            students. Turn on what you need; leave the rest.
          </p>
        </FadeUp>

        <div className="border-t border-ink/10">
          {CHAPTERS.map((c, i) => (
            <div
              key={c.n}
              className="grid grid-cols-1 gap-8 border-b border-ink/10 py-12 md:grid-cols-12 md:items-center md:gap-10 md:py-16"
              data-testid={`chapter-${c.n}`}
            >
              <div className="md:col-span-1">
                <span className="numeral-outline font-display text-6xl font-900 md:text-7xl">{c.n}</span>
              </div>

              <FadeUp className={`md:col-span-5 ${i % 2 ? "md:order-3" : ""}`} delay={0.05}>
                <span className="inline-block rounded-full border border-leaf/40 bg-leaf/10 px-3 py-1 text-xs font-600 uppercase tracking-wide text-leaf-600">
                  {c.tag}
                </span>
                <h3 className="mt-5 font-display text-3xl font-800 tracking-tight text-ink md:text-4xl">{c.title}</h3>
                <p className="mt-4 max-w-md text-lg leading-relaxed text-ink/60">{c.line}</p>
              </FadeUp>

              <div className={`md:col-span-6 ${i % 2 ? "md:order-2" : ""}`}>
                <ClipReveal className="overflow-hidden rounded-2xl">
                  <img src={c.img} alt={c.title} className="h-[260px] w-full object-cover md:h-[320px]" />
                </ClipReveal>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function SafetyFeatures() {
  return (
    <section id="safety" className="bg-bone pt-4 pb-24 md:pt-6 md:pb-36">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <FadeUp className="mb-10 max-w-2xl md:mb-14">
          <span className="text-xs font-600 uppercase tracking-widest text-leaf-600">Safety First</span>
          <h2 className="mt-4 font-display text-[clamp(2rem,4.5vw,3.4rem)] font-800 leading-[1.02] tracking-tightest text-ink">
            Every child leaves with the right adult, every single day.
          </h2>
          <p className="mt-5 text-lg leading-relaxed text-ink/60">
            Dismissal is the riskiest fifteen minutes of the school day. CampusPath makes it
            accountable, verified and calm, from the first bell to the last bus.
          </p>
        </FadeUp>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {SAFETY_FEATURES.map((s, i) => {
            const Icon = SAFETY_ICONS[s.icon];
            return (
              <FadeUp
                key={s.title}
                delay={i * 0.06}
                className="group rounded-2xl border border-ink/10 bg-white p-7 transition-transform duration-300 hover:-translate-y-1"
                data-testid={`safety-card-${i}`}
              >
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-leaf/10 text-leaf-600 transition-colors duration-300 group-hover:bg-leaf group-hover:text-ink">
                  {Icon ? <Icon size={22} strokeWidth={2} /> : null}
                </div>
                <h3 className="mt-5 font-display text-xl font-800 tracking-tight text-ink">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-ink/60">{s.line}</p>
              </FadeUp>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function Roles() {
  return (
    <section className="bg-ink py-24 text-white md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <FadeUp>
          <span className="text-xs font-600 uppercase tracking-widest text-leaf">One platform, five points of view</span>
          <h2 className="mt-4 max-w-3xl font-display text-[clamp(1.9rem,4vw,3rem)] font-800 leading-tight tracking-tightest">
            Each role sees exactly what they should, and nothing they shouldn't.
          </h2>
        </FadeUp>
        <div className="mt-14 grid grid-cols-1 gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-5">
          {ROLES.map((r, i) => (
            <FadeUp key={r.role} delay={i * 0.06} className="bg-ink p-7">
              <div className="font-display text-xl font-800 text-leaf">{r.role}</div>
              <p className="mt-4 text-sm leading-relaxed text-white/60">{r.line}</p>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function Stories() {
  return (
    <section id="stories" className="bg-bone py-24 md:py-36">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <FadeUp className="mb-14 max-w-2xl">
          <span className="text-xs font-600 uppercase tracking-widest text-leaf-600">Success Stories</span>
          <h2 className="mt-4 font-display text-[clamp(2rem,4.5vw,3.4rem)] font-800 leading-[1.02] tracking-tightest text-ink">
            Schools feel the difference by the first bell.
          </h2>
        </FadeUp>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {STATS.map((s, i) => (
            <FadeUp key={s.label} delay={i * 0.07} className="flex flex-col justify-between rounded-2xl border border-ink/10 bg-white p-7">
              <div className="font-display text-5xl font-900 tracking-tightest text-ink md:text-6xl">{s.value}</div>
              <div className="mt-6">
                <div className="text-sm font-600 text-ink">{s.label}</div>
                <div className="mt-1 text-xs uppercase tracking-wide text-ink/40">{s.school}</div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function Security() {
  return (
    <section id="security" className="relative overflow-hidden bg-ink py-24 text-white md:py-32">
      <VideoBackground
        src={VIDEOS.dataCenter.src}
        poster={VIDEOS.dataCenter.poster}
        videoClassName="opacity-20 [filter:grayscale(30%)]"
        overlayClassName="bg-gradient-to-br from-ink/92 via-ink/88 to-ink"
      />
      <div className="relative z-10 mx-auto grid max-w-7xl gap-14 px-6 md:px-8 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <FadeUp>
            <span className="text-xs font-600 uppercase tracking-widest text-leaf">Data Protection</span>
            <h2 className="mt-4 font-display text-[clamp(2rem,4vw,3.2rem)] font-800 leading-[1.03] tracking-tightest">
              Security designed for modern schools.
            </h2>
            <p className="mt-6 max-w-md text-lg leading-relaxed text-white/60">
              Built around privacy, role-based access, reliable records and secure workflows, so schools
              manage the day with confidence.
            </p>
          </FadeUp>
          <FadeUp delay={0.1} className="mt-10 rounded-2xl border border-white/10 bg-white/5 p-6 backdrop-blur">
            <div className="flex items-center justify-between">
              <span className="text-xs uppercase tracking-widest text-white/50">Security Center</span>
              <span className="flex items-center gap-2 text-xs font-700 text-leaf"><span className="h-1.5 w-1.5 animate-pulse rounded-full bg-leaf" /> SECURE</span>
            </div>
            <div className="mt-5 grid grid-cols-2 gap-3">
              {["Data Encryption", "Two-Factor Auth", "Role-Based Access", "Daily Backups"].map((x) => (
                <div key={x} className="flex items-center gap-2 rounded-xl bg-white/5 px-3 py-2.5 text-sm">
                  <span className="text-leaf"><svg width="16" height="16" viewBox="0 0 24 24" fill="none"><path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" /></svg></span>
                  {x}
                </div>
              ))}
            </div>
          </FadeUp>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <div className="grid gap-px overflow-hidden rounded-2xl border border-white/10 bg-white/10 sm:grid-cols-2">
            {SECURITY.map((s, i) => (
              <FadeUp key={s.title} delay={i * 0.06} className="bg-ink p-7">
                <div className="font-display text-lg font-800 text-white">{s.title}</div>
                <p className="mt-3 text-sm leading-relaxed text-white/55">{s.line}</p>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function TestGuardTeaser() {
  return (
    <section className="relative overflow-hidden bg-obsidian py-24 text-white md:py-32">
      <div className="pointer-events-none absolute inset-0 opacity-30">
        <img src={IMAGES.lock} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-obsidian/70" />
      </div>
      <div className="relative mx-auto max-w-7xl px-6 md:px-8">
        <FadeUp className="max-w-2xl">
          <span className="text-xs font-600 uppercase tracking-widest text-leaf">Companion Product</span>
          <h2 className="mt-4 font-display text-[clamp(2rem,4.5vw,3.6rem)] font-900 leading-[0.98] tracking-tightest">
            Meet <TGWordmark />.
          </h2>
          <p className="mt-6 text-lg leading-relaxed text-white/60">
            Device monitoring and browser lockdown for secure exams, works in tandem with CampusPath, or
            entirely on its own.
          </p>
          <div className="mt-9">
            <CTA href={testguardHref()} variant="leafDark" dataTestId="testguard-teaser-btn">Explore TestGuard</CTA>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

function TrialBand() {
  return (
    <section className="bg-bone py-24 md:py-32">
      <div className="mx-auto max-w-7xl px-6 md:px-8">
        <FadeUp className="relative overflow-hidden rounded-[2rem] border border-ink/10 bg-ink px-8 py-16 text-white md:px-16 md:py-24">
          <div className="pointer-events-none absolute -right-16 -top-10 select-none opacity-[0.08]">
            <span className="font-display text-[16rem] font-900 leading-none text-white">45</span>
          </div>
          <div className="relative max-w-2xl">
            <h2 className="font-display text-[clamp(2.2rem,5vw,4rem)] font-900 leading-[0.98] tracking-tightest">
              Try it free. <br /> On your schedule.
            </h2>
            <p className="mt-6 text-lg leading-relaxed text-white/65">
              Pick a trial that fits your calendar, anywhere from <span className="text-leaf font-700">45 to 90 days</span>, 
              and run a real dismissal, start to finish. No pressure, no card.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <CTA to="/trial" variant="leafDark" dataTestId="band-trial-btn">Start Free Trial</CTA>
              <CTA to="/demo" variant="ghostDark" dataTestId="band-demo-btn">Schedule a Demo</CTA>
            </div>
          </div>
        </FadeUp>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main className="bg-bone">
      <SEO
        title="CampusPath, Smart Dismissal. Safer Schools."
        description="The K-12 school dismissal and operations platform, verified pickups, real-time attendance, gradebook and family communication in one place. Start a 45–90 day free trial."
        canonical="https://campuspath.org/"
        keywords="school dismissal software, K-12 dismissal management, student dismissal system, school operations platform, verified pickup"
      />
      <SiteNav />
      <Hero />
      <Marquee text="Smart Dismissal. Safer Schools." />
      <Chapters />
      <SafetyFeatures />
      <Security />
      <Stories />
      <Roles />
      <TestGuardTeaser />
      <TrialBand />
      <SiteFooter />
    </main>
  );
}
